import React, {
    FC,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';

import { keyframes, styled } from '../theme';

const ModalContext = createContext<{ onHide: () => void } | undefined>(undefined);

export const ModalProvider = ({
    onHide,
    children,
}: {
    onHide: () => void;
    children: React.ReactNode;
}) => {
    return <ModalContext.Provider value={{ onHide }}>{children}</ModalContext.Provider>;
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};

const slideDown = keyframes`
    from {
        transform: translateY(-25%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
`;

const slideUp = keyframes`
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(-25%);
        opacity: 0;
    }
`;

const ModalWrapper = styled('div')<{ isVisible: boolean }>`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: ${({ theme }) => theme.zIndex.modal};
    overflow-y: auto;
    visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

const Backdrop = styled('div')<{ isVisible: boolean }>`
    position: fixed;
    inset: 0;
    background: rgba(22, 25, 28, 0.85);
    z-index: ${({ theme }) => theme.zIndex.backdrop};
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    transition: opacity 0.3s ease;
    pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
`;

const ModalContent = styled('div')<{ show: boolean }>`
    position: relative;
    background: ${({ theme }) => theme.color.white};
    width: 600px;
    max-width: 90%;
    border-radius: ${({ theme }) => theme.borderRadius.base}px;
    margin: 30px auto;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    z-index: ${({ theme }) => theme.zIndex.modal};
    overflow: visible;
    max-height: none;
    animation: ${({ show }) => (show ? slideDown : slideUp)} 0.3s ease-out forwards;
`;

const Modal: FC<{
    show?: boolean;
    onHide?: () => void;
    onEnter?: () => void;
    children?: React.ReactNode;
    className?: string;
}> = ({ show = false, onHide, onEnter, children, className }) => {
    const [isVisible, setIsVisible] = useState(show);
    const [isAnimating, setIsAnimating] = useState(false);
    const modalRef = useRef<HTMLDivElement | null>(null);

    const handleAnimationEnd = useCallback(() => !show && setIsAnimating(false), [show]);

    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setIsAnimating(true);
            document.body.style.overflow = 'hidden';
            onEnter?.();
        } else if (isVisible) {
            setIsAnimating(true);
            requestAnimationFrame(() => {
                setTimeout(() => {
                    setIsAnimating(false);
                    setIsVisible(false);
                    document.body.style.overflow = '';
                }, 300);
            });
        }
    }, [show, onEnter]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && show) onHide?.();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, onHide]);

    if (!isVisible && !isAnimating) return null;

    const modalRoot = document.getElementById('popover-root');

    if (!modalRoot) return null;

    return ReactDOM.createPortal(
        <ModalProvider onHide={onHide}>
            <Backdrop isVisible={show || isAnimating} onClick={onHide} />
            <ModalWrapper isVisible={show || isAnimating}>
                <ModalContent
                    ref={modalRef}
                    className={className}
                    show={show}
                    onAnimationEnd={handleAnimationEnd}
                >
                    {children}
                </ModalContent>
            </ModalWrapper>
        </ModalProvider>,
        modalRoot
    );
};

export default Modal;
